import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import Modal from 'LuxKit/Modal/Modal'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { EmptyArray } from 'lib/array/arrayUtils'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import React, { useCallback, useContext, useMemo } from 'react'
import ExperimentalSearchTileFeaturesList from '../shared/ExperimentalSearchTileFeaturesList'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useSearchTileBedbankRate from 'hooks/Search/useSearchTileBedbankRate'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BedbankCancellationPolicy from '../../../OfferPage/Bedbank/BedbankCancellationPolicy'
import useOffer from 'hooks/Offers/useOffer'
import ExperimentalHotelUpgradableInclusionsLabel from './ExperimentalHotelUpgradableInclusionsLabel'

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary
  offerMetaData?: App.OfferListMetaData
  searchTileBedbankRate: ReturnType<typeof useSearchTileBedbankRate>
}

function ExperimentalHotelSearchTileFeatures({
  bestPackage,
  filters,
  offer,
  offerMetaData,
  searchTileBedbankRate,
}: Props) {
  const metaData = useOfferMetaData(offer.id, filters)

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const { isBedbankRate, bedbankRateForRoom, isFetchingBedbankRates } = searchTileBedbankRate

  const [bedbankOffer] = useOffer<App.BedbankOffer>(isBedbankRate ? metaData?.pricing.ratePropertyId : undefined)

  const bedbankInclusions = useMemo<Array<App.OfferInclusion>>(() => {
    if (!bedbankRateForRoom?.inclusions?.length) return EmptyArray
    return bedbankRateForRoom.inclusions.filter((inclusion: App.OfferInclusion) => inclusion.description !== 'Room only')
  }, [bedbankRateForRoom?.inclusions])

  const featuresToDisplay = useMemo<{
    featureList: Array<App.OfferInclusion>
    featureType: 'inclusions' | 'amenities'
  }>(() => {
    if (bedbankInclusions.length > 0) {
      return { featureList: bedbankInclusions, featureType: 'inclusions' }
    }

    if (bonusInclusions.length > 0) {
      return { featureList: bonusInclusions, featureType: 'inclusions' }
    }

    if (inclusions.length > 0) {
      return { featureList: inclusions, featureType: 'inclusions' }
    }

    const amenities = offer.lowestPricePackage?.roomType.amenities ?? []
    if (amenities.length > 0) return { featureList: amenities, featureType: 'amenities' }

    return { featureList: EmptyArray, featureType: 'inclusions' }
  }, [bedbankInclusions, bonusInclusions, inclusions, offer.lowestPricePackage?.roomType.amenities])

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
    bundledOfferId: offerMetaData?.bundledOfferId,
  })

  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionText="View offer"
      primaryActionTo={offerUrl}
    >
      <Group direction="vertical" gap={12}>
        <OfferInclusionsList type="lux-plus" inclusions={luxPlusInclusions} />
        {featuresToDisplay && <OfferInclusionsList inclusions={featuresToDisplay.featureList} />}
      </Group>
    </Modal>)
  }, [luxPlusInclusions, offer, featuresToDisplay, offerUrl, showModal])

  const cancellationPolicyType = bestPackage?.roomRate?.cancellationPolicy?.type

  const upgradableInclusions = useMemo<Array<App.OfferInclusion>>(() => {
    if (!offer.packages.length) return EmptyArray

    // Find the package with the most inclusions
    const packageWithMostInclusions = offer.packages.reduce((max, pkg) => {
      const maxCount = max.inclusions?.length ?? 0
      const pkgCount = pkg.inclusions?.length ?? 0
      return pkgCount > maxCount ? pkg : max
    }, offer.packages[0])

    const mostInclusions = packageWithMostInclusions.inclusions ?? EmptyArray
    if (!mostInclusions.length) return EmptyArray

    const inclusionsToDisplay = featuresToDisplay.featureType === 'inclusions' ? featuresToDisplay.featureList : []
    // Filter out inclusions that are already displayed or duplicated
    const displayedDescriptions = new Set(inclusionsToDisplay.map(i => i.description))
    const seenDescriptions = new Set<string>()

    const bonusInclusions = mostInclusions.filter(inclusion => {
      const desc = inclusion.description
      if (displayedDescriptions.has(desc) || seenDescriptions.has(desc)) return false
      seenDescriptions.add(desc)
      return true
    })

    return bonusInclusions.length ? bonusInclusions : EmptyArray
  }, [offer.packages, featuresToDisplay])

  const saveAmount = !!metaData?.pricing?.lowestMemberPrice && !!metaData?.pricing?.lowestPrice ?
    metaData?.pricing?.lowestPrice - metaData?.pricing?.lowestMemberPrice : undefined

  return (
    <Group className={ExperimentalHotelSearchTileFeatures.name} direction="vertical" gap={8} noDisplayWhenEmpty>
      {isBedbankRate && isFetchingBedbankRates && (
        <VerticalSpacer gap={4}>
          <TextLoadingBox typography="body-small" width="60%" />
          <TextLoadingBox typography="body-small" width="80%" />
          <TextLoadingBox typography="body-small" width="40%" />
        </VerticalSpacer>
      )}
      {!(isBedbankRate && isFetchingBedbankRates) && (
        <>
          <ExperimentalSearchTileFeaturesList
            features={featuresToDisplay.featureList}
            featuresType={featuresToDisplay.featureType}
            cancellationPolicy={
              <>
                {!!cancellationPolicyType && !isBedbankRate && (
                  <TileCancellationPolicy
                    icon={<LineCalendarIcon />}
                    cancellationPolicyType={cancellationPolicyType}
                    checkInDate={filters?.checkIn}
                    timezoneOffset={offer.property.timezoneOffset}
                    offerType={isBedbankRate ? 'bedbank_hotel' : offer.type}
                  />
                )}
                {isBedbankRate && bedbankOffer && bedbankRateForRoom && (
                  <BedbankCancellationPolicy
                    offer={bedbankOffer}
                    rate={bedbankRateForRoom}
                    showNonCancellation={false}
                    icon={<LineCalendarIcon />}
                  />
                )}
              </>
            }
            onMoreClick={openAllInclusionsModal}
          />

          {!!luxPlusInclusions?.length && (
            <LuxPlusLabelWithModal
              type="member-inclusions"
              inclusions={luxPlusInclusions}
              offer={offer}
              hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
            />
          )}
          {!!saveAmount && saveAmount > 0 && !luxPlusInclusions?.length && (
            <LuxPlusLabelWithModal
              type="member-price-offer"
              offer={offer}
              saveAmount={saveAmount}
            />
          )}
          {!!upgradableInclusions?.length && <ExperimentalHotelUpgradableInclusionsLabel offer={offer} inclusions={upgradableInclusions} filters={filters} />}
        </>
      )}
    </Group>
  )
}

export default ExperimentalHotelSearchTileFeatures
